/**
 * BulkCreatorDetail — dedicated full-page workspace for a single creator inside
 * a bulk campaign. Replaces the old inline row-expand which was unusable at
 * scale (100+ creators). Renders the CreatorBotPanel (Personal / Mandates /
 * Approvals) alongside a full-height WhatsApp conversation feed and keeps
 * the ShippingAddressDialog trigger available so the CM can still capture the
 * shipping address from here.
 *
 * Route: /bulk/campaigns/:campaignId/creators/:creatorId
 * Roles: campaign_manager | admin | account_executive (also manager / agency)
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { ArrowLeft, Loader2, Instagram, MapPin, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { CreatorBotPanel } from '../../components/campaign/CreatorBotPanel';
import { ShippingAddressDialog } from '../../components/ShippingAddressDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mirror the STATUS_META vocabulary from BulkCampaignWorkspace so the status
// chip on the detail page speaks the same language as the roster row.
const STATUS_META = {
  invited:           { label: 'Invited',            color: '#71717a' },
  interested:        { label: 'Interested',         color: '#0055FF' },
  rejected:          { label: 'Rejected',           color: '#FF3B30' },
  no_response:       { label: 'No Response',        color: '#52525b' },
  approved_by_brand: { label: 'Approved by Brand',  color: '#FFB800' },
  product_shipped:   { label: 'Product Shipped',    color: '#A855F7' },
  product_delivered: { label: 'Product Delivered',  color: '#EC4899' },
  draft_submitted:   { label: 'Draft Submitted',    color: '#06B6D4' },
  revision_required: { label: 'Revision Required',  color: '#F97316' },
  approved:          { label: 'Approved',           color: '#84CC16' },
  posted:            { label: 'Posted',             color: '#22C55E' },
  completed:         { label: 'Completed',          color: '#00C972' },
};

const fmtNum = (n) => {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
};

export const BulkCreatorDetail = () => {
  const { campaignId, creatorId } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [row, setRow] = useState(null); // bulk_creators row
  const [loading, setLoading] = useState(true);
  const [addressOpen, setAddressOpen] = useState(false);

  const reload = async () => {
    const [{ data: c }, { data: rows }] = await Promise.all([
      axios.get(`${API_URL}/api/bulk/campaigns/${campaignId}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/bulk/campaigns/${campaignId}/creators`, { withCredentials: true }),
    ]);
    setCampaign(c);
    const found = (rows || []).find((r) => r.id === creatorId || r.creator_id === creatorId);
    if (!found) {
      toast.error('Creator not found in this campaign');
      navigate(`/bulk/campaigns/${campaignId}`);
      return;
    }
    setRow(found);
  };

  useEffect(() => {
    (async () => {
      try {
        await reload();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load creator');
        navigate(`/bulk/campaigns/${campaignId}`);
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, creatorId]);

  const statusMeta = useMemo(() => STATUS_META[row?.status] || STATUS_META.invited, [row?.status]);
  const hasAddress = row?.shipping_address
    && typeof row.shipping_address === 'object'
    && row.shipping_address.address_line_1;

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center bg-white dark:bg-[#0A0B0D]">
          <Loader2 className="animate-spin text-[#FF451A]" size={32} />
        </div>
      </div>
    );
  }
  if (!row) return null;

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="bulk-creator-detail-page">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(`/bulk/campaigns/${campaignId}`)}
            className="text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF451A] flex items-center gap-1 mb-6"
            data-testid="bulk-creator-detail-back"
          >
            <ArrowLeft size={14} /> Back to campaign
          </button>

          {/* Creator header */}
          <div className="bg-white dark:bg-[#131518] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6 mb-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-white text-xl font-bold shrink-0">
                  {(row.creator_name || 'C')[0]?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-bold tracking-tight text-[#0F0F0F] dark:text-white truncate" data-testid="bulk-creator-detail-name">
                      {row.creator_name}
                    </h1>
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border"
                      style={{
                        color: statusMeta.color,
                        borderColor: `${statusMeta.color}55`,
                        backgroundColor: `${statusMeta.color}14`,
                      }}
                      data-testid="bulk-creator-detail-status"
                    >
                      {statusMeta.label}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-[#6B6B6B] dark:text-zinc-400 flex items-center gap-3 flex-wrap">
                    {row.instagram_handle && (
                      <a
                        href={`https://instagram.com/${row.instagram_handle.replace(/^@/, '')}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 hover:text-[#E1306C]"
                      >
                        <Instagram size={12} /> @{row.instagram_handle}
                      </a>
                    )}
                    {row.instagram_followers != null && (
                      <span className="font-mono text-xs">{fmtNum(row.instagram_followers)} followers</span>
                    )}
                    {row.category && (
                      <span className="px-2 py-0.5 rounded-full bg-[#FF451A]/10 text-[#FF451A] text-[10px] font-medium">
                        {row.category}
                      </span>
                    )}
                  </div>
                  {campaign && (
                    <div className="mt-1 text-[11px] font-mono uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500">
                      {campaign.campaign_name}
                    </div>
                  )}
                </div>
              </div>

              {/* Address trigger — mirrors the row-level chip so the CM can capture
                  or edit the shipping address from the detail page itself. */}
              <button
                onClick={() => setAddressOpen(true)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 border transition-colors ${
                  hasAddress
                    ? 'bg-[#00C972]/10 text-[#00C972] border-[#00C972]/30 hover:bg-[#00C972]/20'
                    : 'bg-[#FF451A]/10 text-[#FF451A] border-[#FF451A]/30 hover:bg-[#FF451A]/20'
                }`}
                data-testid="bulk-creator-detail-address-button"
              >
                {hasAddress ? <><MapPin size={12} /> Edit address</> : <><Plus size={12} /> Add address</>}
              </button>
            </div>
          </div>

          {/* Body — CreatorBotPanel already renders Personal / Mandates / Approvals
              plus the WhatsApp conversation column on the right. */}
          <div className="bg-white dark:bg-[#131518] border border-black/[0.08] dark:border-white/5 rounded-2xl overflow-hidden" data-testid="bulk-creator-detail-body">
            <CreatorBotPanel
              creatorId={row.creator_id}
              brandCampaignId={campaignId}
              row={row}
              onRowUpdated={reload}
            />
          </div>
        </div>
      </div>

      <ShippingAddressDialog
        row={addressOpen ? row : null}
        onClose={() => setAddressOpen(false)}
        onSaved={() => { setAddressOpen(false); reload(); }}
      />
    </div>
  );
};

export default BulkCreatorDetail;
